/**
 * "Company & plan" on My account: the signed-in user's own company, its tier, and who its
 * Admins are, read-only, for every staff role. Plan and usage are the Admin-only block
 * below it (the existing YourPlanSection, unchanged), shown only to roles the backend lets
 * read them.
 */

import { memo, useCallback, useEffect, useState } from 'react';
import { Alert, Button, Spin, Typography } from 'antd';
import axios from '../../../../../../axiosConfig';
import YourPlanSection from '../../../../../settings/components/your-plan-section';
import { UserRole } from '../../../../utils/role';
import { CompanyProfile } from '../../types';
import styles from './index.module.css';

const { Text } = Typography;

const TIER_LABEL: Record<NonNullable<CompanyProfile['tier']>, string> = {
  SMB: 'SMB',
  ENTERPRISE: 'Enterprise',
};

/** Roles the backend lets read plan and usage (Admin and Super Admin only). */
const PLAN_ROLES: ReadonlyArray<UserRole | undefined> = [
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
];

const fullName = (c: CompanyProfile['adminContacts'][number]) =>
  [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || 'Admin';

interface Props {
  customerId: number;
  role?: UserRole;
}

const CompanySection = ({ customerId, role }: Props) => {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    axios
      .get<CompanyProfile>(`/api/customers/${customerId}/company-profile`)
      .then((res) => {
        if (!cancelled) setProfile(res.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  useEffect(() => load(), [load]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spin />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Your company details could not be loaded."
        action={
          <Button size="small" onClick={load}>
            Try again
          </Button>
        }
      />
    );
  }

  const admins = profile.adminContacts ?? [];

  return (
    <div>
      <dl className={styles.facts}>
        <div>
          <dt className={styles.label}>Company</dt>
          <dd>{profile.name || '—'}</dd>
        </div>
        {profile.tier && (
          <div>
            <dt className={styles.label}>Tier</dt>
            <dd>{TIER_LABEL[profile.tier] ?? profile.tier}</dd>
          </div>
        )}
      </dl>

      <div className={styles.contacts}>
        <Text strong>Admin contacts</Text>
        {admins.length === 0 ? (
          <p className={styles.empty}>
            No active Admin is listed for your company. Contact Avrio support at{' '}
            <a href="mailto:support@goavrio.com">support@goavrio.com</a>.
          </p>
        ) : (
          <ul className={styles.list}>
            {admins.map((admin) => (
              <li key={admin.email ?? fullName(admin)}>
                {fullName(admin)}
                {admin.email && (
                  <>
                    {' · '}
                    <a href={`mailto:${admin.email}`}>{admin.email}</a>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
        <Text type="secondary" className={styles.hint}>
          To change your company&apos;s settings or users, ask one of them.
        </Text>
      </div>

      {PLAN_ROLES.includes(role) && <YourPlanSection customerId={customerId} />}
    </div>
  );
};

export default memo(CompanySection);
